import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Save, Plus, Trash2, Loader2, Star } from "lucide-react";
import { toast } from "sonner";

const TestimonialsEditor = ({ content, onSave }) => {
  const [formData, setFormData] = useState({
    section_label: "",
    title: "",
    subtitle: "",
    testimonials: []
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (content) {
      setFormData(content);
    }
  }, [content]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleTestimonialChange = (index, field, value) => {
    const updated = [...formData.testimonials];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, testimonials: updated });
  };

  const addTestimonial = () => {
    const newTestimonial = {
      id: `testimonial-${Date.now()}`,
      name: "Nombre del Cliente",
      company: "Empresa",
      role: "Cargo",
      content: "Testimonio del cliente...",
      rating: 5
    };
    setFormData({ ...formData, testimonials: [...formData.testimonials, newTestimonial] });
  };

  const removeTestimonial = (index) => {
    const updated = formData.testimonials.filter((_, i) => i !== index);
    setFormData({ ...formData, testimonials: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const success = await onSave(formData);
    setSaving(false);
    if (success) {
      toast.success("Testimonios actualizados correctamente");
    } else {
      toast.error("Error al guardar los cambios");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="testimonials-editor"
    >
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Editar Testimonios</h1>
          <p className="text-[#94a3b8]">Administra los testimonios de clientes</p>
        </div>
        <button onClick={addTestimonial} className="btn-secondary flex items-center gap-2">
          <Plus className="w-5 h-5" />
          <span>Agregar Testimonio</span>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="glass-card p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Textos de la Sección</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[#94a3b8] text-sm mb-2">Etiqueta</label>
              <input type="text" name="section_label" value={formData.section_label} onChange={handleChange} className="contact-input w-full" />
            </div>
            <div>
              <label className="block text-[#94a3b8] text-sm mb-2">Título</label>
              <input type="text" name="title" value={formData.title} onChange={handleChange} className="contact-input w-full" />
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-[#94a3b8] text-sm mb-2">Subtítulo</label>
            <textarea name="subtitle" value={formData.subtitle} onChange={handleChange} rows={2} className="contact-input w-full resize-none" />
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {formData.testimonials.map((testimonial, index) => (
            <div key={testimonial.id} className="glass-card p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => handleTestimonialChange(index, "rating", star)}
                      className={star <= testimonial.rating ? "text-yellow-400" : "text-[#94a3b8]"}
                    >
                      <Star className={`w-4 h-4 ${star <= testimonial.rating ? "fill-current" : ""}`} />
                    </button>
                  ))}
                </div>
                <button type="button" onClick={() => removeTestimonial(index)} className="p-1 text-red-400 hover:bg-red-500/10 rounded">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-[#94a3b8] text-sm mb-1">Nombre</label>
                  <input type="text" value={testimonial.name} onChange={(e) => handleTestimonialChange(index, "name", e.target.value)} className="contact-input w-full" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[#94a3b8] text-sm mb-1">Empresa</label>
                    <input type="text" value={testimonial.company} onChange={(e) => handleTestimonialChange(index, "company", e.target.value)} className="contact-input w-full" />
                  </div>
                  <div>
                    <label className="block text-[#94a3b8] text-sm mb-1">Cargo</label>
                    <input type="text" value={testimonial.role} onChange={(e) => handleTestimonialChange(index, "role", e.target.value)} className="contact-input w-full" />
                  </div>
                </div>
                <div>
                  <label className="block text-[#94a3b8] text-sm mb-1">Testimonio</label>
                  <textarea value={testimonial.content} onChange={(e) => handleTestimonialChange(index, "content", e.target.value)} rows={3} className="contact-input w-full resize-none" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end">
          <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            <span>{saving ? "Guardando..." : "Guardar Cambios"}</span>
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default TestimonialsEditor;
